/**
 * Create form for a school year.
 */
import * as React from 'react';
import { FileText } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { DatePicker } from '@/components/forms/DatePicker';
import { Checkbox } from '@/components/forms/Checkbox';
import { createSchoolYear, getSchoolYears } from '@/services/school-years.service';
import type { CreateSchoolYearDto } from '@/types/school-year.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { schoolYearValidationSchema } from '@/utils/validation-schemas-utils';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { useAuthStore } from '@/stores/authStore';
import { SCHOOL_YEAR_FORM_HINTS, SCHOOL_YEAR_FORM_QUERY_KEYS } from './school-year-configs';
import {
  applySchoolYearFormField,
  buildCreateSchoolYearDto,
  emptyCreateSchoolYearForm,
  formatEstablishmentObjectiveInput,
  parseEstablishmentObjective,
  withSchoolYearIntegrityContext,
} from './school-year-form-utils';

/** Props for {@link CreateSchoolYearView}. */
interface CreateSchoolYearViewProps {
  onClose: () => void;
  onSuccess?: () => void;
}

/** Create form for a school year. */
export const CreateSchoolYearView: React.FC<CreateSchoolYearViewProps> = ({
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = React.useState<CreateSchoolYearDto>(emptyCreateSchoolYearForm);

  const validation = useFormValidation(schoolYearValidationSchema);
  const { roles: currentUserRoles } = useAuthStore();
  const currentUserRoleCode = currentUserRoles?.[0]?.code;
  const { data: yearsResponse } = useQuery({
    queryKey: SCHOOL_YEAR_FORM_QUERY_KEYS.integrity,
    queryFn: () => getSchoolYears({ page: 1, pageSize: 200 }),
  });
  const existingYears = yearsResponse?.data ?? [];

  /** Attach existing years so the schema can reject overlapping ranges. */
  const withIntegrityContext = React.useCallback(
    (data: CreateSchoolYearDto) => withSchoolYearIntegrityContext(data, existingYears),
    [existingYears],
  );
  const formDataRef = React.useRef(formData);
  formDataRef.current = formData;
  const validateFormRef = React.useRef(validation.validateForm);
  validateFormRef.current = validation.validateForm;

  /** Re-validate overlap once the existing-years list arrives. */
  React.useEffect(() => {
    const current = formDataRef.current;
    if (existingYears.length === 0) return;
    if (!current.code && !current.startDate && !current.endDate) return;
    validateFormRef.current(withIntegrityContext(current));
  }, [existingYears, withIntegrityContext]);

  const createMutation = useMutation({
    mutationFn: (dto: CreateSchoolYearDto) => createSchoolYear(dto),
    onSuccess: () => {
      toast.success('Année scolaire créée avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const isSubmitting = createMutation.isPending;

  /** Patch one field and re-validate against existing years. */
  const handleFieldChange = (
    field: keyof CreateSchoolYearDto,
    value: string | boolean | number | undefined,
  ) => {
    const updatedData = applySchoolYearFormField(formData, field, value);
    setFormData(updatedData);
    validation.validateField(field, value, withIntegrityContext(updatedData));
    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Validate overlap then POST create DTO (objective is admin-only). */
  const handleSubmit = () => {
    if (!validation.validateForm(withIntegrityContext(formData))) return;
    createMutation.mutate(buildCreateSchoolYearDto(formData, currentUserRoleCode));
  };

  return (
    <SlidePanelForm
      formType="create"
      title="Créer une année scolaire"
      subtitle="Renseignez les informations de l'année scolaire"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={isSubmitting}
      isValid={validation.isFormValid()}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Informations générales
        </h4>

        <Input
          label="Code"
          value={formData.code}
          onChange={(e) => handleFieldChange('code', e.target.value)}
          onBlur={() => handleBlur('code')}
          placeholder="Ex: 2025-2026"
          maxLength={20}
          required
          helperText={SCHOOL_YEAR_FORM_HINTS.code}
          state={validation.getFieldState('code')}
          error={validation.getFieldError('code')}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DatePicker
            label="Date de début"
            value={formData.startDate}
            onChange={(value) => handleFieldChange('startDate', value)}
            onBlur={() => handleBlur('startDate')}
            required
            helperText={SCHOOL_YEAR_FORM_HINTS.startDate}
            state={validation.getFieldState('startDate')}
            error={validation.getFieldError('startDate')}
          />

          <DatePicker
            label="Date de fin"
            value={formData.endDate}
            onChange={(value) => handleFieldChange('endDate', value)}
            onBlur={() => handleBlur('endDate')}
            minDate={formData.startDate}
            required
            helperText={SCHOOL_YEAR_FORM_HINTS.endDate}
            state={validation.getFieldState('endDate')}
            error={validation.getFieldError('endDate')}
            align="right"
          />
        </div>

        <Checkbox
          label="Année en cours"
          checked={formData.isCurrent || false}
          onCheckedChange={(checked) => handleFieldChange('isCurrent', checked)}
        />

        {currentUserRoleCode === 'admin' && (
          <Input
            label="Objectif établissement"
            type="number"
            min={0}
            value={formatEstablishmentObjectiveInput(formData.establishmentObjective)}
            onChange={(e) => {
              handleFieldChange(
                'establishmentObjective',
                parseEstablishmentObjective(e.target.value),
              );
            }}
            placeholder="Ex: 50"
            helperText="Nombre d'établissements visé pour cette année scolaire (admin uniquement)"
          />
        )}
      </div>
    </SlidePanelForm>
  );
};
